// useWeeklyCreditTrend — Phase 6 credit-burn ticker.
//
// Compares this-week vs last-week point spend (negative deltas in
// `point_transactions`) so the chat composer can whisper "🔥 You're
// using N% more credits this week — that's the path to mastery."
//
// Backed by `my_weekly_credit_trend` SQL function (SECURITY DEFINER,
// filtered to auth.uid()). Returns nulls for guests so callers can
// gracefully render nothing.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface WeeklyCreditTrend {
  thisWeekUsed: number;
  lastWeekUsed: number;
  /** Percent change vs last week. `null` when last week was zero. */
  pctChange: number | null;
}

export const useWeeklyCreditTrend = () => {
  const { user } = useAuth();
  const [data, setData] = useState<WeeklyCreditTrend | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setData(null);
      return;
    }
    setLoading(true);
    (async () => {
      const { data: rows, error } = await supabase.rpc("my_weekly_credit_trend");
      if (cancelled) return;
      if (error || !rows || !Array.isArray(rows) || rows.length === 0) {
        setData(null);
      } else {
        const r = rows[0] as {
          this_week_used: number | string;
          last_week_used: number | string;
          pct_change: number | string | null;
        };
        setData({
          thisWeekUsed: Number(r.this_week_used ?? 0),
          lastWeekUsed: Number(r.last_week_used ?? 0),
          pctChange: r.pct_change == null ? null : Number(r.pct_change),
        });
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return { data, loading };
};